import React, { MouseEvent, ReactNode } from "react";
import DatatableColumnModel, { DatatableColumnAlign } from "./DatatableColumnModel";
import { useState } from "react";
import { useEffect } from "react";
import { Button, Checkbox, InputAdornment, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import "./Datatable.scss";
import { OrderDirection } from "./OrderDirection";
import _ from "lodash";

interface DatatableProps<T> {
    columns: DatatableColumnModel<T>[],
    data: T[],
    pagingType: PagingTypeEnum,
    useDefaultSearchBar?: boolean,
    onSearchValueChange?: (value: string) => void,
    useItemPerPage?: boolean,
    onPageChanged?: any,
    onPageSizeChanged?: any,
    onShowMoreClick?: any,
    customText?: DatatableText,
    showMoreMode_IsLastPage?: boolean,
    isDataLoaded: boolean,
    className?: string,
    useHeaderSort?: boolean,
    orderColumn?: string,
    orderDirection?: OrderDirection,
    onOrderDirectionChange?: (columnName: DatatableColumnModel<T>, orderDirection: OrderDirection) => void,
    selectedRowIndex?: number,
    onRowSelectedChange?: (selectedRow: T, selectedIndex: number, event: MouseEvent<HTMLElement>) => void,
    stickyHeader?: boolean,
    useCheckBox?: boolean,
    onTableCheckboxSelectedChange?: (selectedRows: T[]) => void,
    isDisableRow?: (selectedRow: T) => boolean,
    isDisableRowClassName?: string,
    triggerClearCheckBox?: number,
    checkBoxColumnHeaderClassName?: string
    checkBoxColumnClassName?: string
}
interface DatatableState {
    Data: object[],
    PageSize: number,
}

export enum PagingTypeEnum {
    Standard,
    ShowMore,
    NextBackFireStore,
    None,
}

export interface DatatableText {
    showMoreButtonText: string,
    showMoreLoadingText: string
}

const initDatatableText = {
    showMoreButtonText: "Show more",
    showMoreLoadingText: "Loading"
} as DatatableText;

interface SelectedCheckboxModel<T> {
    model: T,
    index: number
}

const Datatable = <T,>(props: DatatableProps<T>): JSX.Element => {
    const [tableState, setTableState] = useState({
        data: [],
        PageSize: 10
    } as unknown as DatatableState);

    const [selectedCheckboxData, setSelectedCheckboxData] = useState<SelectedCheckboxModel<T>[]>([]);
    const [headerCheckboxChecked, setHeaderCheckboxChecked] = useState<boolean>(false);

    const [textState, setTextState] = useState({
    } as DatatableText);

    useEffect(() => {
        setSelectedCheckboxData([]);
    }, [props.triggerClearCheckBox])

    useEffect(() => {
        if (props.customText) {
            mapCustomText(props.customText);
        } else {
            setTextState(initDatatableText)
        }
    }, []);

    const mapCustomText = (customTextProp: DatatableText) => {
        let text = {} as DatatableText;

        let key: keyof DatatableText;
        for (key in initDatatableText) {
            if (!customTextProp.hasOwnProperty(key) || customTextProp[key] === null || customTextProp[key].length === 0) {
                text[key] = initDatatableText[key];
            } else {
                text[key] = customTextProp[key];
            }
        }

        setTextState(text);
    };

    const renderCell = (row: T, column: DatatableColumnModel<T>) => {
        let key: keyof T;

        for (key in row) {
            if (key == column.Field) {
                return row[key];
            }
        }

        return "";
    };

    return (
        <div className="datatable-wrapper">
            {props.useDefaultSearchBar &&
                <div className="searchContainer">
                    <TextField
                        label="Enter to search"
                        InputProps={{
                            endAdornment: <InputAdornment position="end">
                                <SearchIcon></SearchIcon>
                            </InputAdornment>,
                        }}
                        onKeyUp={(event) => {
                            console.log((event.target as HTMLInputElement).value);
                            if (event.key == 'Enter') {
                                if (props.onSearchValueChange) {
                                    props.onSearchValueChange((event.target as HTMLInputElement).value);
                                }
                            }
                        }}
                    />
                </div>
            }
            <div className={"tableWrapper" + " " + props.className ? props.className : ""}>
                <Table className="datatable">
                    <TableHead>
                        <TableRow className="datatable-head">
                            {
                                props.useCheckBox === true &&
                                <TableCell
                                    className={"datatable-cell-header " + (props.checkBoxColumnHeaderClassName ? props.checkBoxColumnHeaderClassName : "")}
                                >
                                    <Checkbox
                                        className="datatable-checkbox"
                                        checked={headerCheckboxChecked}
                                        onChange={(event, checked: boolean) => {
                                            setHeaderCheckboxChecked(checked);
                                            if (checked) {
                                                setSelectedCheckboxData(
                                                    _.map(props.data, (row: T, index: number) => {
                                                        return {
                                                            model: row,
                                                            index: index
                                                        } as SelectedCheckboxModel<T>
                                                    })
                                                );
                                                if (props.onTableCheckboxSelectedChange) {
                                                    props.onTableCheckboxSelectedChange(props.data);
                                                }
                                            } else {
                                                setSelectedCheckboxData([]);
                                                if (props.onTableCheckboxSelectedChange) {
                                                    props.onTableCheckboxSelectedChange([]);
                                                }
                                            }
                                        }} />
                                </TableCell>
                            }
                            {
                                props.columns.map((column: DatatableColumnModel<T>) => {
                                    let headerSortClass = props.orderColumn === column.Field ?
                                        "datatable-header-sort-wrapper-active" :
                                        "datatable-header-sort-wrapper";

                                    let tableCellHeader = props.useHeaderSort && column.AllowSort ?
                                        "datatable-cell-header datatable-cell-sortable" :
                                        "datatable-cell-header";

                                    if (column.CellHeaderClassName) {
                                        tableCellHeader += " " + column.CellHeaderClassName;
                                    }

                                    return <TableCell
                                        onClick={() => {
                                            if (column.AllowSort) {
                                                if (props.onOrderDirectionChange) {
                                                    let sortDirection = column.Field !== props.orderColumn ?
                                                        props.orderDirection === undefined ? OrderDirection.Asc : props.orderDirection :
                                                        props.orderDirection === OrderDirection.Asc ? OrderDirection.Desc : OrderDirection.Asc;

                                                    props.onOrderDirectionChange(column, sortDirection);
                                                }
                                            }
                                        }}
                                        className={tableCellHeader}
                                        width={column.Width}
                                        align={
                                            column.Align == DatatableColumnAlign.Left ? "left" :
                                                column.Align == DatatableColumnAlign.Center ? "center" :
                                                    column.Align == DatatableColumnAlign.Right ? "right" : "inherit"
                                        }>
                                        {column.Title}
                                        {
                                            props.useHeaderSort &&
                                            column.AllowSort &&
                                            <span className={headerSortClass}>
                                                {
                                                    props.orderDirection === OrderDirection.Desc &&
                                                    <Button
                                                        className={"datatable-header-sort-button"}
                                                    >
                                                        <i className="fa fa-sort-desc" aria-hidden="true"></i>
                                                    </Button>
                                                }
                                                {
                                                    props.orderDirection !== OrderDirection.Desc &&
                                                    <Button
                                                        className={"datatable-header-sort-button"}
                                                    >
                                                        <i className="fa fa-sort-asc" aria-hidden="true"></i>
                                                    </Button>
                                                }
                                            </span>
                                        }
                                    </TableCell>
                                })
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            props.data.map((row: T, index: number) => {
                                let disableRowClassName = "";
                                if (props.isDisableRow) {
                                    if (props.isDisableRow(row) && props.isDisableRowClassName) {
                                        disableRowClassName = props.isDisableRowClassName;
                                    }
                                }

                                let rowClass = "datatable-row " + disableRowClassName;
                                if (props.selectedRowIndex === index) {
                                    rowClass += rowClass + " datatable-row-selected";
                                }

                                return <TableRow
                                    className={rowClass}
                                    onClick={(event: MouseEvent<HTMLElement>) => {
                                        if (event.target as HTMLInputElement) {
                                            if ((event.target as HTMLInputElement).type === "checkbox") {
                                                return;
                                            }
                                        }

                                        if (props.onRowSelectedChange) {
                                            props.onRowSelectedChange(row, index, event);
                                        }
                                    }}
                                >
                                    {
                                        props.useCheckBox === true &&
                                        <TableCell
                                            className={"datatable-cell " + (props.checkBoxColumnClassName ? props.checkBoxColumnClassName : "")}
                                        >
                                            <Checkbox
                                                className={"datatable-checkbox"}
                                                checked={_.some(selectedCheckboxData, (checkboxData: SelectedCheckboxModel<T>) => {
                                                    return checkboxData.index === index
                                                })}
                                                onChange={(event, checked: boolean) => {
                                                    let selectedCheckboxDataClone = [...selectedCheckboxData];
                                                    if (checked) {
                                                        selectedCheckboxDataClone.push({
                                                            model: row,
                                                            index: index
                                                        } as SelectedCheckboxModel<T>);
                                                    } else {
                                                        let selectedIndex = _.findIndex(selectedCheckboxDataClone, (item) => {
                                                            return item.index === index;
                                                        });

                                                        selectedCheckboxDataClone.splice(selectedIndex, 1);

                                                        setHeaderCheckboxChecked(false);
                                                    }
                                                    setSelectedCheckboxData(selectedCheckboxDataClone);

                                                    if (props.onTableCheckboxSelectedChange) {
                                                        props.onTableCheckboxSelectedChange(
                                                            _.map(selectedCheckboxDataClone, (item: SelectedCheckboxModel<T>) => {
                                                                return item.model
                                                            })
                                                        );
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                    }
                                    {
                                        props.columns.map((column: DatatableColumnModel<T>) => (
                                            <TableCell
                                                className={"datatable-cell" + (column.CellClassName ? (" " + column.CellClassName) : "")}
                                                align={
                                                    column.Align == DatatableColumnAlign.Left ? "left" :
                                                        column.Align == DatatableColumnAlign.Center ? "center" :
                                                            column.Align == DatatableColumnAlign.Right ? "right" : "inherit"
                                                }
                                                onClick={() => {
                                                    if (column.onCellClick) {
                                                        column.onCellClick(row);
                                                    }
                                                }}
                                            >
                                                {column.Render && column.Render(row, index)}
                                                {!column.Render && renderCell(row, column)}
                                            </TableCell>
                                        ))
                                    }
                                </TableRow>
                            })
                        }
                    </TableBody>
                </Table>
                <div className={"pagingArea"}>
                    {
                        props.useItemPerPage &&
                        <div>
                            <span>Items per pages:</span>
                            <Select
                                value={tableState.PageSize}
                                onChange={(e) => {
                                    setTableState(prev => ({
                                        ...prev,
                                        PageSize: e.target.value as number
                                    }))

                                    if (props.onPageSizeChanged) {
                                        props.onPageSizeChanged(tableState.PageSize);
                                    }
                                }}
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </Select>
                        </div>
                    }
                    {
                        props.pagingType === PagingTypeEnum.ShowMore && !props.showMoreMode_IsLastPage &&
                        <div className={"datatable-show-more-area"}>
                            <div>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        if (props.onShowMoreClick) {
                                            props.onShowMoreClick();
                                        }
                                    }}
                                    disabled={!props.isDataLoaded}
                                >
                                    {props.isDataLoaded && textState.showMoreButtonText}
                                    {!props.isDataLoaded && textState.showMoreLoadingText}
                                </Button>
                            </div>
                        </div>
                    }

                </div>
            </div>
        </div>
    )
};

const areEqual = <T,>(prevProps: DatatableProps<T>, nextProps: DatatableProps<T>): boolean => {
    const isEqual = (a: any[], b: any[]): boolean => {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (JSON.stringify(a[i]) !== JSON.stringify(b[i])) return false;
        }
        return true;
    };

    return (
        isEqual(prevProps.columns, nextProps.columns) &&
        isEqual(prevProps.data, nextProps.data) &&
        prevProps.pagingType == nextProps.pagingType &&
        prevProps.useDefaultSearchBar == nextProps.useDefaultSearchBar &&
        prevProps.customText == nextProps.customText &&
        prevProps.className == nextProps.className &&
        prevProps.useHeaderSort == nextProps.useHeaderSort &&
        prevProps.orderColumn == nextProps.orderColumn &&
        prevProps.orderDirection == nextProps.orderDirection &&
        prevProps.selectedRowIndex == nextProps.selectedRowIndex &&
        prevProps.stickyHeader == nextProps.stickyHeader &&
        prevProps.useCheckBox == nextProps.useCheckBox &&
        prevProps.isDisableRow == nextProps.isDisableRow &&
        prevProps.isDisableRowClassName == nextProps.isDisableRowClassName &&
        prevProps.triggerClearCheckBox == nextProps.triggerClearCheckBox &&
        prevProps.checkBoxColumnHeaderClassName == nextProps.checkBoxColumnHeaderClassName &&
        prevProps.checkBoxColumnClassName == nextProps.checkBoxColumnClassName &&

        prevProps.useItemPerPage == nextProps.useItemPerPage &&
        prevProps.onPageChanged == nextProps.onPageChanged &&
        prevProps.onPageSizeChanged == nextProps.onPageSizeChanged &&
        prevProps.onShowMoreClick == nextProps.onShowMoreClick &&
        prevProps.showMoreMode_IsLastPage == nextProps.showMoreMode_IsLastPage &&
        prevProps.isDataLoaded == nextProps.isDataLoaded
    );
};


// Memoize the Datatable component
const MemoizedDatatable = React.memo(Datatable, areEqual) as <T>(props: DatatableProps<T>) => JSX.Element;;

export default MemoizedDatatable;